import { MontantCalculDirective } from '../../../directives/montant-calcul.directive';
import {
  Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output,
  SimpleChanges, ViewChild, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Compte } from '../../../models/compte.model';
import { Categorie } from '../../../models/categorie.model';
import { FiltresRechercheOperations, ResultatRechercheOperations } from '../../../models/recherche-operation.model';
import { ComptesService } from '../../../services/comptes.service';

interface FormulaireRecherche {
  libelle: string;
  modeLibelle: NonNullable<FiltresRechercheOperations['modeLibelle']>;
  categorie: string;
  type: '' | 'ENTREE' | 'SORTIE';
  modeMontant: 'EXACT' | 'PLAGE' | 'POURCENTAGE' | 'EUROS';
  montant: number | null;
  toleranceEuros: number | null;
  montantMin: number | null;
  montantMax: number | null;
  modeDate: 'EXACT' | 'PLAGE';
  date: string;
  dateDebut: string;
  dateFin: string;
  compteId: number | null;
  moyenPaiement: string;
}

@Component({
  selector: 'app-popup-recherche-operation',
  imports: [CommonModule, FormsModule, MontantCalculDirective],
  templateUrl: './popup-recherche-operation.component.html',
  styleUrl: './popup-recherche-operation.component.scss',
})
export class PopupRechercheOperationComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() compteId: number | null = null;
  @Input() comptes: Compte[] = [];
  @Input() categories: Categorie[] = [];
  @Input() moyensPaiement: readonly { label: string; value: string }[] = [];
  @Output() fermer = new EventEmitter<void>();
  @ViewChild('libelleInput') libelleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('resultatsZone') resultatsZone?: ElementRef<HTMLElement>;

  filtres = this.filtresVides();
  readonly resultat = signal<ResultatRechercheOperations | null>(null);
  readonly chargement = signal(false);
  readonly erreur = signal<string | null>(null);
  private derniereRecherche: FiltresRechercheOperations | null = null;
  private requete?: Subscription;
  private focusInitial?: HTMLElement;

  constructor(private readonly comptesService: ComptesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.focusInitial = document.activeElement as HTMLElement;
        this.reinitialiser();
        setTimeout(() => {
          if (this.visible) this.libelleInput?.nativeElement.focus();
        });
      } else {
        this.effacerResultats();
        const focus = this.focusInitial;
        setTimeout(() => focus?.isConnected && focus.focus());
      }
    }
  }

  ngOnDestroy(): void { this.requete?.unsubscribe(); }

  reinitialiser(): void {
    this.filtres = this.filtresVides();
    this.filtres.compteId = this.compteId;
    this.effacerResultats();
  }

  effacerResultats(): void {
    this.requete?.unsubscribe();
    this.resultat.set(null);
    this.erreur.set(null);
    this.chargement.set(false);
    this.derniereRecherche = null;
  }

  rechercher(formulaire?: NgForm): void {
    this.effacerResultats();
    if (formulaire?.invalid) {
      this.erreur.set('Vérifiez les montants et les calculs : le résultat doit être positif ou nul.');
      return;
    }
    const f = this.filtres;
    const plageTolerance = this.plageMontant;
    if ((f.modeMontant === 'POURCENTAGE' || f.modeMontant === 'EUROS') && f.montant !== null && !plageTolerance) {
      this.erreur.set(f.modeMontant === 'EUROS' && f.toleranceEuros === null
        ? 'Renseignez la tolérance en euros (zéro ou plus).'
        : 'Vérifiez le montant et la tolérance : valeurs positives, avec au plus deux décimales.');
      return;
    }
    if (f.modeMontant === 'PLAGE' && f.montantMin !== null && f.montantMax !== null && f.montantMin > f.montantMax) {
      this.erreur.set('Le montant minimum doit être inférieur ou égal au maximum.');
      return;
    }
    if (f.modeDate === 'PLAGE' && f.dateDebut && f.dateFin && f.dateDebut > f.dateFin) {
      this.erreur.set('La date de début doit précéder la date de fin.');
      return;
    }
    this.derniereRecherche = {
      libelle: f.libelle.trim() || undefined, modeLibelle: f.modeLibelle,
      categorie: f.categorie || undefined, type: f.type || undefined,
      compteId: f.compteId ?? undefined, moyenPaiement: f.moyenPaiement || undefined,
      ...(f.modeMontant === 'EXACT'
        ? { montant: f.montant ?? undefined }
        : f.modeMontant === 'PLAGE'
          ? { montantMin: f.montantMin ?? undefined, montantMax: f.montantMax ?? undefined }
          : plageTolerance ? { montantMin: plageTolerance.min, montantMax: plageTolerance.max } : {}),
      ...(f.modeDate === 'EXACT'
        ? { dateDebut: f.date || undefined, dateFin: f.date || undefined }
        : { dateDebut: f.dateDebut || undefined, dateFin: f.dateFin || undefined }),
    };
    this.chargerPage(1);
  }

  chargerPage(page: number): void {
    if (!this.derniereRecherche || this.chargement() || page < 1) return;
    this.chargement.set(true);
    this.erreur.set(null);
    this.requete = this.comptesService.rechercherOperations(this.derniereRecherche, page).subscribe({
      next: (resultat) => {
        this.resultat.set(resultat);
        this.chargement.set(false);
        setTimeout(() => {
          if (this.visible) this.resultatsZone?.nativeElement.focus();
        });
      },
      error: (error) => {
        this.chargement.set(false);
        this.erreur.set(error?.error?.message || 'Impossible de rechercher les opérations. Réessayez.');
      },
    });
  }

  get nombrePages(): number {
    const resultat = this.resultat();
    return resultat ? Math.max(1, Math.ceil(resultat.total / resultat.taillePage)) : 1;
  }

  get plageMontant(): { min: number; max: number } | null {
    const f = this.filtres;
    if (f.modeMontant !== 'POURCENTAGE' && f.modeMontant !== 'EUROS') return null;
    const montantMaxCentimes = 999999999999;
    const enCentimes = (valeur: number | null): number | null => {
      if (valeur === null || !Number.isFinite(valeur) || valeur < 0 || valeur > montantMaxCentimes / 100) return null;
      const centimes = Math.round(valeur * 100);
      return Math.abs(valeur * 100 - centimes) < 0.001 ? centimes : null;
    };
    const montant = enCentimes(f.montant);
    if (montant === null) return null;
    let min: number;
    let max: number;
    if (f.modeMontant === 'POURCENTAGE') {
      // Bornes inclusives au centime : ne pas élargir la tolérance par arrondi.
      min = Math.ceil(montant * 9 / 10);
      max = Math.floor(montant * 11 / 10);
    } else {
      const tolerance = enCentimes(f.toleranceEuros);
      if (tolerance === null) return null;
      min = montant - tolerance;
      max = montant + tolerance;
    }
    return { min: Math.max(0, min) / 100, max: Math.min(montantMaxCentimes, max) / 100 };
  }

  get aideLibelle(): string {
    switch (this.filtres.modeLibelle) {
      case 'EXACT': return 'Libellé complet, sans distinction de majuscules. Les caractères * et ? sont littéraux.';
      case 'REGEX': return 'Expression sans délimiteurs /…/. Ex. : ^(loyer|charges). Sans distinction de majuscules.';
      case 'APPROCHANT': return 'Sans accents ni majuscules. Tolère 1 faute à partir de 4 lettres, 2 à partir de 8. Tous les mots sont recherchés.';
      default: return 'Texte contenu dans le libellé. * remplace plusieurs caractères, ? un seul. Ex. : *CARREFOUR*. Sans distinction de majuscules.';
    }
  }

  moyenPaiement(valeur: string | null): string {
    return this.moyensPaiement.find((moyen) => moyen.value === valeur)?.label ?? valeur ?? '—';
  }

  gererClavier(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.fermer.emit();
    } else if (event.key === 'Tab') {
      const panel = event.currentTarget as HTMLElement;
      const elements = Array.from(panel.querySelectorAll<HTMLElement>('button:not(:disabled), input, select'));
      const premier = elements[0];
      const dernier = elements[elements.length - 1];
      if (event.shiftKey && (event.target === premier || event.target === this.resultatsZone?.nativeElement)) {
        event.preventDefault();
        dernier?.focus();
      } else if (!event.shiftKey && event.target === dernier) {
        event.preventDefault();
        premier?.focus();
      }
    }
  }

  private filtresVides(): FormulaireRecherche {
    return {
      libelle: '', modeLibelle: 'JOKERS', categorie: '', type: '',
      modeMontant: 'EXACT', montant: null, toleranceEuros: null, montantMin: null, montantMax: null,
      modeDate: 'PLAGE', date: '', dateDebut: '', dateFin: '',
      compteId: null, moyenPaiement: '',
    };
  }
}
